import { Button } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconPlus } from '@tabler/icons-react';
import { useMemo } from 'react';
import { useProjectId } from '~/hooks/context/useProjectId';
import type { TestListItem } from '~/server/api/routers/test';
import { api } from '~/trpc-extra/api';
import { DataTable } from '../tables/common/DataTable';
import DataTableFilterControl from '../tables/common/DataTableFilterControl';
import { DataTableResultCount } from '../tables/common/DataTableResultCount';
import DataTableRowsSelected from '../tables/common/DataTableRowsSelected';
import DataTableSearchBox from '../tables/common/DataTableSearchBox';
import { useDataTable } from '../tables/common/useDataTable';
import { testColumns } from '../tables/test/testColumns';
import NoTestsMessage from '../welcome/NoTestsMessage';
import { TestTypeIcon } from './TestTypeIcon';

export default function AddTestsModal({ tests, testSuiteId }: { tests: TestListItem[]; testSuiteId: string }) {
  const projectId = useProjectId();
  const trpcUtils = api.useUtils();
  const { mutateAsync: updateTestMembership } = api.testSuite.updateTests.useMutation({
    onSuccess: () => {
      notifications.show({
        color: 'teal',
        message: `${selectedTestIds.length} test${selectedTestIds.length === 1 ? '' : 's'} added`,
      });
      trpcUtils.testSuite.list.invalidate().catch(() => undefined);
      trpcUtils.testSuite.listWithLatestRun.invalidate().catch(() => undefined);
      trpcUtils.testSuite.get.invalidate().catch(() => undefined);
      trpcUtils.test.listTests.invalidate().catch(() => undefined);
    },
    onError: (error) => {
      notifications.show({
        color: 'red',
        title: 'Error',
        message: `${error}`,
      });
    },
  });
  const { data: userFlows = [] } = api.userFlow.list.useQuery({ projectId: projectId! }, { enabled: !!projectId });
  const { data: tags = [] } = api.tag.list.useQuery({ projectId: projectId! }, { enabled: !!projectId });

  const noActionTable = useMemo(
    () => testColumns({ userFlows, tags, tests }).filter((col) => col.id !== 'actions'),
    [userFlows, tags],
  );

  const table = useDataTable({
    columns: noActionTable,
    data: tests,
    // Disable row selection for mobile app tests
    enableRowSelection: (row) => row.original.testType !== 'MOBILE_APP',
  });

  const selectedTestIds = table.getFilteredSelectedRowModel().rows.map((row) => row.original.id);
  const selectedCount = selectedTestIds.length;

  return (
    <div>
      <div className="flex h-16 flex-row items-center justify-between">
        <div className="flex flex-row items-center gap-2">
          <DataTableSearchBox table={table} />
          <DataTableFilterControl table={table} />
          <DataTableResultCount table={table} />
          {selectedCount > 0 && <DataTableRowsSelected table={table} />}
        </div>
        {selectedCount > 0 && (
          <Button
            color="blue"
            title="Add Tests"
            leftSection={<IconPlus size={16} />}
            onClick={async () => {
              modals.closeAll();
              await updateTestMembership({
                operation: 'add',
                projectId: projectId!,
                testSuiteId,
                testIds: selectedTestIds,
              });
            }}
          >
            Add {selectedCount} test
            {selectedCount === 1 ? '' : 's'}
          </Button>
        )}
      </div>
      <DataTable
        table={table}
        onRowClick={(row) => {
          // Show user mobile app is not available
          if (!row.getCanSelect()) {
            notifications.show({
              title: (
                <div className="flex items-center gap-2">
                  <TestTypeIcon
                    type="MOBILE_APP"
                    iconSize={20}
                    className="text-[var(--mantine-color-default-color)]"
                    withinPortal={false}
                  />
                  <span className="font-bold">Coming Soon!</span>
                </div>
              ),
              message: (
                <div>
                  Your workspace does not support running <span className="font-bold">Mobile App</span> tests as part of
                  a test suite. Please contact the Stably team to enable it.
                </div>
              ),
            });
            return;
          }

          row.toggleSelected();
        }}
        emptyMessage={<NoTestsMessage />}
      />
    </div>
  );
}
